import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Loader2, Play, Sparkles, ShoppingCart, X, Store, Waypoints } from "lucide-react";
import { Button } from "../ui/button";
import { config } from "../../Environment";
import useRetryFetch from "../../hooks/useRetryFetch";
import useProcess from "../../hooks/useProcess";
import { useSocketContext } from "../../context/SocketProvider";
import { tryGetUserProfile } from "../../api/auth";
import useAuth from "../../hooks/useAuth";
import { trackPurchaseComplete } from "../../lib/analytics";
import { trackPurchaseCompleteWithData } from "../../lib/appTracking";

interface PaymentIframeProps {
  open: boolean;
  paymentUrl: string;
  onSuccess: (data: { processId?: string; paymentId: string }) => void;
  onError: (error: string) => void;
  onClose: () => void;
  /**
   * Guest checkout (no authenticated session). Skips the auth-only payment/product
   * detail fetch and shows a "check your email to set your password" success screen.
   */
  guestMode?: boolean;
}

interface PaymentData {
  _id: string;
  status: string;
  type: string;
  amount?: number;
  items: Array<{
    fiProduct: string;
    name: string;
    description: string;
    custom?: {
      processId?: string;
      isRenewal?: boolean;
      linkActivityUserId?: string;
    };
  }>;
}

interface ProductData {
  _id: string;
  type: string;
  data?: {
    activity?: {
      fiWorkflow: string;
    };
    pwf?: {
      fiPwfTemplate: string;
    };
  };
  fiVendor?: string;
}

export function PaymentIframe({
  open,
  paymentUrl,
  onSuccess,
  onError,
  onClose,
  guestMode = false,
}: PaymentIframeProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();
  const { startProcess, resumeProcess } = useProcess();
  const { socket } = useSocketContext();
  const { auth, setUser } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [productData, setProductData] = useState<ProductData | null>(null);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [trialProcessId, setTrialProcessId] = useState<string | null>(null);
  const [isRenewal, setIsRenewal] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isFreeProduct = paymentData != null && paymentData.amount === 0;
  const successDetailsReady = paymentStatus === "paid" && !fetchingDetails && paymentData != null;

  // Refresh user profile (to get updated roles after subscription purchase)
  const refreshUserProfile = useCallback(async () => {
    try {
      const updatedUser = await tryGetUserProfile(retryFetch);
      if (updatedUser) {
        console.log("[PaymentIframe] Refreshed user profile with updated roles:", updatedUser.roles);
        setUser(updatedUser);
      }
    } catch (error) {
      console.error("[PaymentIframe] Error refreshing user profile:", error);
    }
  }, [retryFetch, setUser]);

  // Fetch product details
  const fetchProduct = useCallback(async (paymentId: string, productId: string) => {
    try {
      const response = await retryFetch(
        `${config.API_BASE}/api/product/findPurchased?paymentId=${encodeURIComponent(
          paymentId
        )}&productId=${encodeURIComponent(productId)}`
      );

      if (response.ok) {
        const result = await response.json();
        if (result.type === "item") {
          setProductData(result.item);

          // If this is a subscription product, refresh user profile to get updated roles
          if (result.item.type === "subscription") {
            console.log("[PaymentIframe] Subscription purchase detected, refreshing user profile...");
            await refreshUserProfile();
          }
        }
      }
    } catch (error) {
      console.error("Error fetching product:", error);
    }
  }, [retryFetch, refreshUserProfile]);

  // Fetch payment details
  const fetchPayment = useCallback(async (paymentId: string) => {
    try {
      setFetchingDetails(true);
      const response = await retryFetch(
        `${config.API_BASE}/api/payment?id=${encodeURIComponent(paymentId)}`
      );

      if (response.ok) {
        const result = await response.json();
        if (result.type === "item") {
          setPaymentData(result.item);

          // Fetch product info for the first item
          if (result.item.type === "product" && result.item.items?.[0]?.fiProduct) {
            await fetchProduct(paymentId, result.item.items[0].fiProduct);
          }

          // Track purchase completion
          // GA4 - privacy-safe (no personal data)
          trackPurchaseComplete(
            result.item.amount || 0,
            result.item.currency || 'USD',
            'credit_card'
          );

          // Internal tracking - with full details
          trackPurchaseCompleteWithData({
            paymentId: result.item._id,
            productName: result.item.items?.[0]?.name,
            productType: result.item.type,
            userEmail: auth?.email,
            amount: result.item.amount || 0,
            currency: result.item.currency || 'USD',
            method: 'credit_card',
          });
        }
      }
    } catch (error) {
      console.error("Error fetching payment:", error);
    } finally {
      setFetchingDetails(false);
    }
  }, [retryFetch, fetchProduct]);

  useEffect(() => {
    if (!open) {
      setIsLoading(true);
      setPaymentStatus(null);
      setPaymentData(null);
      setProductData(null);
      setFetchingDetails(false);
      setTrialProcessId(null);
      setIsRenewal(false);
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
      return;
    }

    // Listen for postMessage from iframe (PayProGlobal redirect)
    const handlePostMessage = async (event: MessageEvent) => {
      console.log("[PaymentIframe] Received postMessage:", event.data);

      // Handle payment completion from iframe
      if (event.data.type === "paymentComplete") {
        const { status, paymentId, productId, trialProcessId: trialProcId } = event.data;

        console.log("[PaymentIframe] Payment complete via postMessage:", {
          status,
          paymentId,
          productId,
          trialProcessId: trialProcId,
        });

        setIsLoading(false);
        setPaymentStatus(status);

        // Store trialProcessId if provided
        if (trialProcId) {
          setTrialProcessId(trialProcId);
        }

        if (status === "paid" && paymentId) {
          window.dispatchEvent(
            new CustomEvent("clary:celebration", {
              detail: { key: "first_purchase" },
            })
          );
          // Guests have no session — skip the auth-only detail fetch and just show
          // the "check your email" success screen.
          if (!guestMode) {
            await fetchPayment(paymentId);
          }
        } else if (status === "declined") {
          onError("Payment was declined. Please try again.");
        } else if (status === "review") {
          onError("Payment is under review. Please check back later.");
        } else {
          onError(`Payment status: ${status}`);
        }
      }

      // Handle close modal request from iframe
      if (event.data.type === "closePaymentModal") {
        console.log("[PaymentIframe] Received closePaymentModal message");
        // If success screen is showing, don't auto-close immediately — let the user see it
        if (paymentStatus === "paid") {
          return;
        }
        onClose();
      }
    };

    // Register postMessage listener
    window.addEventListener("message", handlePostMessage);

    if (!socket) {
      console.warn("[PaymentIframe] Socket not available");
      // Don't return early - postMessage can work without socket
    }

    // Listen for WebSocket payment:complete event (backup method)
    const handlePaymentComplete = async (data: any) => {
      console.log("[PaymentIframe] Received payment:complete event via WebSocket:", data);

      setIsLoading(false);
      setPaymentStatus(data.status);

      // Store trialProcessId if provided
      if (data.trialProcessId) {
        setTrialProcessId(data.trialProcessId);
      }

      if (data.status === "paid") {
        // Payment successful - fetch details
        if (data.paymentId && !guestMode) {
          setFetchingDetails(true);

          // Fetch payment details
          try {
            const response = await retryFetch(
              `${config.API_BASE}/api/payment?id=${encodeURIComponent(data.paymentId)}`
            );

            if (response.ok) {
              const result = await response.json();
              if (result.type === "item") {
                setPaymentData(result.item);

                // Check if this is a renewal payment
                const isRenewalPayment = result.item.items?.[0]?.custom?.isRenewal === true;
                setIsRenewal(isRenewalPayment);

                // If productId is provided in event data, use it directly
                // Otherwise fall back to getting it from payment items
                const productId = data.productId || result.item.items?.[0]?.fiProduct;

                if (result.item.type === "product" && productId) {
                  await fetchProduct(data.paymentId, productId);
                }

                // Track purchase completion
                // GA4 - privacy-safe (no personal data)
                trackPurchaseComplete(
                  result.item.amount || 0,
                  result.item.currency || 'USD',
                  'credit_card'
                );

                // Internal tracking - with full details
                trackPurchaseCompleteWithData({
                  paymentId: result.item._id,
                  productName: result.item.items?.[0]?.name,
                  productType: result.item.type,
                  userEmail: auth?.email,
                  amount: result.item.amount || 0,
                  currency: result.item.currency || 'USD',
                  method: 'credit_card',
                });
              }
            }
          } catch (error) {
            console.error("Error fetching payment:", error);
          } finally {
            setFetchingDetails(false);
          }
        }
      } else if (data.status === "declined") {
        onError("Payment was declined. Please try again.");
      } else if (data.status === "review") {
        onError("Payment is under review. Please check back later.");
      } else {
        onError(`Payment status: ${data.status}`);
      }
    };

    // Register WebSocket listener (if socket is available)
    if (socket) {
      socket.on("payment:complete", handlePaymentComplete);
    }

    // Cleanup
    return () => {
      window.removeEventListener("message", handlePostMessage);
      if (socket) {
        socket.off("payment:complete", handlePaymentComplete);
      }
    };
  }, [open, socket, onError, retryFetch, fetchPayment, paymentStatus, paymentData, onSuccess, onClose, guestMode]);

  // Auto-close success screen after a delay once details are loaded
  useEffect(() => {
    if (successDetailsReady) {
      autoCloseTimerRef.current = setTimeout(() => {
        if (paymentData?._id) {
          onSuccess({ paymentId: paymentData._id });
        }
        onClose();
      }, 4000);

      return () => {
        if (autoCloseTimerRef.current) {
          clearTimeout(autoCloseTimerRef.current);
          autoCloseTimerRef.current = null;
        }
      };
    }
  }, [successDetailsReady, paymentData, onSuccess, onClose]);

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  // Opens the buyer's newly-cloned PWF — fulfillment (processPwfOrder) runs
  // server-side on the same request that marks the payment "paid", so by the
  // time this success screen is showing the clone already exists; look it up
  // via the same instance-status check the product page uses for "already
  // owned" (see marketplace/product/index.tsx's checkUserOwnsPwf).
  const handleOpenPwf = async () => {
    const templateId = productData?.data?.pwf?.fiPwfTemplate;
    if (!templateId) return;
    try {
      const response = await retryFetch(`${config.API_BASE}/api/pwf/${templateId}/instance-status`);
      const result = await response.json();
      if (result.owned && result.instanceId) {
        navigate(`/pwf?id=${result.instanceId}`);
        onClose();
      } else {
        onError(t("failed_to_open_pwf") || "Your PWF is still being set up — check your PWF Hub in a moment.");
      }
    } catch (err) {
      console.error("[PaymentIframe] Failed to look up new PWF instance:", err);
      onError(t("failed_to_open_pwf") || "Failed to open your new PWF");
    }
  };

  // Action handlers
  const handleStartActivity = async () => {
    if (!productData?.data?.activity?.fiWorkflow) return;

    // For renewal, just close the dialog and call onSuccess to refresh the list
    if (isRenewal) {
      if (paymentData?._id) {
        onSuccess({ paymentId: paymentData._id });
      }
      onClose();
      return;
    }

    // If there's a trial process to claim, resume it instead of starting new
    if (trialProcessId) {
      await resumeProcess({
        processId: trialProcessId,
        onError: () => {
          onError("An error occurred while resuming the activity.");
        },
      });
    } else {
      await startProcess({
        prescriptionId: productData.data.activity.fiWorkflow,
        onError: () => {
          onError("An error occurred while starting the activity.");
        },
      });
    }

    // Close dialog after starting activity
    onClose();
  };

  const handleContinueProcess = async () => {
    // Use trialProcessId from postMessage if available, otherwise fall back to custom.processId
    const processId = trialProcessId || paymentData?.items?.[0]?.custom?.processId;
    if (!processId) return;

    await resumeProcess({
      processId,
      onError: () => {
        onError("An error occurred while continuing the process.");
      },
    });

    // Close dialog after resuming
    onClose();
  };

  const handleCloseSuccess = () => {
    // Call onSuccess to refresh the activity list if needed
    if (paymentData?._id) {
      onSuccess({ paymentId: paymentData._id });
    }
    // Just close the dialog, user stays on current page
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      if (!newOpen && !paymentStatus) {
        // User closed dialog before payment completed
        onClose();
      }
    }}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {paymentStatus === "paid"
              ? (fetchingDetails ? t("loading") : (isFreeProduct ? t("added_successfully") : t("payment_successful")))
              : t("complete_payment")}
          </DialogTitle>
          <DialogDescription>
            {paymentStatus === "paid"
              ? (fetchingDetails ? "" : (isFreeProduct ? t("item_added_to_your_account") : t("redirecting_to_activity")))
              : t("enter_payment_details_to_continue")}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center flex-1">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">{t("loading_payment_page")}</span>
          </div>
        )}

        {paymentStatus === "paid" && (
          <div className="flex flex-col items-center justify-center flex-1 text-center px-4">
            <div className="bg-green-100 text-green-800 rounded-full p-4 mb-4">
              <svg
                className="h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2">
              {fetchingDetails ? t("loading") : (isFreeProduct ? t("added_successfully") : t("payment_successful"))}
            </h3>

            {guestMode ? (
              <div className="flex flex-col items-center gap-4 my-2">
                <p className="text-sm text-muted-foreground max-w-sm">
                  {t("guest_payment_success") ||
                    "Thank you! Check your email for a link to set your password and access your program."}
                </p>
                <Button variant="outline" onClick={handleCloseSuccess} className="flex items-center">
                  <X className="h-4 w-4 mr-2" />
                  {t("close") || "Close"}
                </Button>
              </div>
            ) : fetchingDetails ? (
              <div className="flex items-center justify-center my-4">
                <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
                <span className="text-sm text-gray-600">{t("loading_details")}</span>
              </div>
            ) : (
              <>
                {paymentData && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-1">
                      {paymentData.items?.[0]?.name || t("purchase_complete")}
                    </p>
                    {isRenewal && (
                      <p className="text-sm text-green-600 font-medium mb-2">
                        {t("activity_renewed_successfully")}
                      </p>
                    )}
                    {!isFreeProduct && (
                      <p className="text-xs text-gray-500">
                        {t("receipt_number")}: {paymentData._id}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex gap-3 mt-6">
                  {productData?.type === "activity" && !isRenewal && (
                    <Button
                      onClick={handleStartActivity}
                      className="flex items-center"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      {t("start_activity")}
                    </Button>
                  )}

                  {(productData?.type === "service" ||
                    productData?.type === "mentor_session_credit") &&
                    paymentData?.items?.[0]?.custom?.processId && (
                    <Button
                      onClick={handleContinueProcess}
                      className="flex items-center"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      {t("continue")}
                    </Button>
                  )}

                  {productData?.type === "activity_generator_credit" && (
                    <Button
                      onClick={handleCloseSuccess}
                      className="flex items-center"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      {t("continue")}
                    </Button>
                  )}

                  {productData?.type === "vendor_activity_credit" && (
                    <Button
                      onClick={handleCloseSuccess}
                      className="flex items-center"
                    >
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      {t("continue_shopping")}
                    </Button>
                  )}

                  {productData?.type === "pwf" && (
                    <Button
                      onClick={handleOpenPwf}
                      className="flex items-center"
                    >
                      <Waypoints className="h-4 w-4 mr-2" />
                      {t("open_pwf") || "Open"}
                    </Button>
                  )}

                  {productData?.type === "subscription" && (
                    <Button
                      onClick={() => {
                        navigate("/commerce");
                        onClose();
                      }}
                      className="flex items-center"
                    >
                      <Store className="h-4 w-4 mr-2" />
                      {t("go_to_vendor_management")}
                    </Button>
                  )}

                  {/* Always show close button */}
                  <Button
                    variant="outline"
                    onClick={handleCloseSuccess}
                    className="flex items-center"
                  >
                    <X className="h-4 w-4 mr-2" />
                    {t("close")}
                  </Button>
                </div>

                {!productData && paymentData && (
                  <Button
                    variant="outline"
                    onClick={handleCloseSuccess}
                    className="flex items-center mt-6"
                  >
                    <X className="h-4 w-4 mr-2" />
                    {t("close")}
                  </Button>
                )}
              </>
            )}
          </div>
        )}

        <iframe
          ref={iframeRef}
          src={paymentUrl}
          className={`flex-1 border-0 rounded ${
            isLoading || paymentStatus ? "hidden" : ""
          }`}
          title="Payment"
          onLoad={handleIframeLoad}
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation"
        />
      </DialogContent>
    </Dialog>
  );
}

export default PaymentIframe;
